import { useMutation } from "@apollo/client";
import { ReprocessMessageInput } from "@clockwise/schema";
import { useUpdateHighlightEvents } from "@clockwise/web-commons/src/util/HighlightEventsContext";
import { TrackingEvents, useTracking } from "@clockwise/web-commons/src/util/analytics.util";
import { useEffect, useRef } from "react";
import {
  ChatDetailsFragmentDoc,
  ChatHistoryQueryDocument,
} from "../../apollo/__generated__/ChatHistory.generated";
import { ReprocessMessageDocument } from "../../apollo/__generated__/ReprocessMessage.generated";
import { GQLAIMessage } from "../../utils/types";
import { useSetAIError } from "../AIErrorContext";
import { useAIMessageContext } from "../AIMessageContext";

export const removeLastProposalOrViewEventsResponse = (messages: GQLAIMessage[]) => {
  // if last message is a proposal response, remove it
  const lastMessage = messages[messages.length - 1];
  if (
    lastMessage.__typename === "ProposalResponse" ||
    lastMessage.__typename === "ViewEventsResponse"
  ) {
    return messages.slice(0, -1);
  }
  return messages;
};

export const useReprocessMessage = (onCompletedCallback?: (timeout?: number) => void) => {
  const {
    setProcessingMessage,
    setCancelProcessing,
    cancelProcessing,
    setReprocessingMessage,
  } = useAIMessageContext();
  const setHighlightEvents = useUpdateHighlightEvents();
  const track = useTracking();
  const setError = useSetAIError();

  const abortController = useRef(new AbortController());

  // This stops the request completely
  const abort = () => {
    abortController.current.abort();
    abortController.current = new AbortController();
  };

  useEffect(() => {
    // If cancelProcessing, abort the process message request
    if (cancelProcessing) {
      abort();
      setCancelProcessing(false);
    }
  }, [cancelProcessing]);

  const [reprocessMessageMutation] = useMutation(ReprocessMessageDocument, {
    context: {
      fetchOptions: {
        signal: abortController.current.signal,
      },
    },
    onError: (error) => {
      setReprocessingMessage(false);

      // if user intentionally cancels the message, don't throw an error
      if (error.message === "The user aborted a request.") {
        setCancelProcessing(false);
        track(TrackingEvents.CHAT.CANCEL.REPROCESS_MESSAGE_ERROR);
        return;
      }

      let errMessage = "Failed update event search filters";
      const timeoutErr = error.message.includes("ESOCKETTIMEDOUT");
      if (timeoutErr) {
        errMessage += " due to a timeout. Please try again later.";
      }

      track(TrackingEvents.CHAT.EVENT_SEARCH.REPROCESSING_FAILURE, { timeoutErr });
      setError({
        error,
        message: "Failed to reprocess chat message",
        userMessage: errMessage,
        showUserMessage: true,
      });

      setProcessingMessage(false);
    },
    // This is still needed for when the return value of reprocessMessage is not the same as the original message
    // For example, when reprocessing a proposal and the response is a text error message
    update: (cache, { data }) => {
      const cachedQueryData = cache.readQuery({ query: ChatHistoryQueryDocument });

      if (!cachedQueryData) {
        track?.("ReprocessMessageMutation: cache update failure");
        return;
      }

      const cachedConversation = cachedQueryData?.viewer?.user?.chatHistory;
      const assistantMessage = data?.reprocessMessage?.message;

      if (cachedConversation?.messages) {
        // Remove the last message that could have had a view events resposne
        //so that the new Assistant message can be added to the cache
        const updatedCachedMessages = removeLastProposalOrViewEventsResponse(
          cachedConversation.messages,
        );

        cache.writeFragment({
          id: cache.identify(cachedConversation),
          fragment: ChatDetailsFragmentDoc,
          fragmentName: "ChatDetails",
          data: {
            ...cachedConversation,
            messages: [...updatedCachedMessages, assistantMessage],
          },
        });
      }
    },
  });

  const reprocessMessage = (input: ReprocessMessageInput) => {
    setProcessingMessage(true);
    setReprocessingMessage(true);
    void reprocessMessageMutation({
      variables: { input },
      onCompleted: (data) => {
        const conversationMessageId = data.reprocessMessage?.message.conversationId;
        track(TrackingEvents.CHAT.EVENT_SEARCH.REPROCESSING_SUCCESS, {
          conversationMessageId: conversationMessageId,
          evenSearchQuery: input,
        });

        const assistantResponse = data?.reprocessMessage?.message;

        if (assistantResponse && assistantResponse.__typename === "ViewEventsResponse") {
          setHighlightEvents(assistantResponse.eventIds);
        }
        onCompletedCallback?.();
        setProcessingMessage(false);
        setReprocessingMessage(false);
      },
    });
  };

  return reprocessMessage;
};
